import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getPermisos, seleccionarEmpresa, getMisAccesos, getUsuariosContexto, GrupoEmpresaDto } from "../services/api";
import logo from "../kyb.ico";
import PlanDeCuentas from "./contable/PlanDeCuentas";
import Monedas from "./contable/Monedas";
import Empresa from "./parametros/Empresa";
import Usuarios from "./parametros/Usuarios";
import GrupoGeneral from "./grupo/GrupoGeneral";
import AccesosPermisos from "./parametros/AccesosPermisos";
import GruposEmpresas from "./gruposEmpresas/GruposEmpresas";
import EmpresasAdmin from "./empresas/EmpresasAdmin";

// ===== Árbol de navegación =====
//
// El árbol (qué se ve en el sidebar) está desacoplado de los permisos (qué autoriza el backend).
// Cada item declara su propia regla de visibilidad:
//   - permiso("clave.original.ver")  -> reusa exactamente la misma clave que ya usaba la pantalla
//                                        antes de este reordenamiento, así los roles ya asignados
//                                        en producción no se ven afectados.
//   - superusuario                   -> visible solo para Superusuario (pantallas nuevas / placeholders
//                                        que todavía no tienen Funcion en el catálogo de permisos).
// El área (grupo del sidebar) se muestra si al menos uno de sus items es visible.
//   - esAdmin                        -> visible para Superusuario, GroupAdmin o EmpresaAdmin del contexto
//                                        activo (lo operativo del día a día: cualquiera de los tres administra
//                                        su propio ámbito). Se calcula reusando /api/Usuarios/contexto.
//   - siempre                        -> visible para cualquier usuario logueado (autogestión, ayuda).

interface VisCtx {
  permisos: string[];
  esSuperUsuario: boolean;
  esAdmin: boolean;
}

const permiso = (clave: string) => (ctx: VisCtx) => ctx.permisos.includes(clave);
const superusuario = (ctx: VisCtx) => ctx.esSuperUsuario;
const esAdmin = (ctx: VisCtx) => ctx.esAdmin;
const siempre = () => true;

interface AreaItem {
  id: string;
  visible: (ctx: VisCtx) => boolean;
}

interface Area {
  id: string;
  icon: string;
  standalone?: boolean; // sin submenú, renderiza contenido directo (solo "dashboard")
  items: AreaItem[];
}

const AREAS: Area[] = [
  { id: "dashboard", icon: "⊞", standalone: true, items: [] },
  {
    id: "grupo", icon: "🏢",
    items: [
      { id: "gruposEmpresas", visible: superusuario },
      { id: "general", visible: permiso("grupo.general.ver") },
    ],
  },
  {
    id: "empresa", icon: "🏬",
    items: [
      { id: "empresasAdmin", visible: superusuario },
      { id: "datosGenerales", visible: permiso("parametros.empresas.ver") },
    ],
  },
  {
    id: "datos", icon: "🗄",
    items: [
      { id: "planDeCuentas", visible: permiso("contable.planDeCuentas.ver") },
      { id: "monedas", visible: permiso("contable.monedas.ver") },
      { id: "cotizaciones", visible: esAdmin },
      { id: "rubrosAnalisis", visible: esAdmin },
      { id: "medidas", visible: esAdmin },
      { id: "documentos", visible: esAdmin },
      { id: "impuestos", visible: esAdmin },
      { id: "listasDatos", visible: esAdmin },
      { id: "tiposAsientos", visible: esAdmin },
      { id: "numeradores", visible: esAdmin },
      { id: "indicesTasas", visible: esAdmin },
      { id: "propiedades", visible: esAdmin },
    ],
  },
  {
    id: "asientos", icon: "📖",
    items: [
      { id: "ingresar", visible: esAdmin },
      { id: "revisar", visible: esAdmin },
      { id: "lotes", visible: esAdmin },
      { id: "diferenciasCotizacion", visible: esAdmin },
      { id: "cierreResultados", visible: esAdmin },
      { id: "cierreAperturaLibros", visible: esAdmin },
      { id: "trabar", visible: esAdmin },
    ],
  },
  {
    id: "operaciones", icon: "🧾",
    items: [
      { id: "ingresarOperaciones", visible: esAdmin },
      { id: "revisarOperaciones", visible: esAdmin },
      { id: "definicionOperaciones", visible: esAdmin },
    ],
  },
  {
    id: "informes", icon: "📈",
    items: [
      { id: "mayores", visible: esAdmin },
      { id: "balances", visible: esAdmin },
      { id: "resultados", visible: esAdmin },
      { id: "saldos", visible: esAdmin },
      { id: "diarios", visible: esAdmin },
    ],
  },
  {
    id: "herramientas", icon: "🛠",
    items: [
      { id: "ordenarAsientos", visible: esAdmin },
      { id: "exportar", visible: esAdmin },
      { id: "recalcularSaldos", visible: esAdmin },
      { id: "opciones", visible: esAdmin },
    ],
  },
  {
    id: "seguridad", icon: "🔒",
    items: [
      { id: "usuariosGrupo", visible: permiso("grupo.usuarios.ver") },
      { id: "usuariosEmpresa", visible: permiso("parametros.usuarios.ver") },
      { id: "rolesPermisos", visible: permiso("parametros.accesos.ver") },
      { id: "cambiarPassword", visible: siempre },
      { id: "auditoria", visible: esAdmin },
    ],
  },
  {
    id: "rrhh", icon: "👥",
    items: [
      { id: "empleados", visible: esAdmin },
      { id: "liquidaciones", visible: esAdmin },
      { id: "ausencias", visible: esAdmin },
    ],
  },
  {
    id: "ayuda", icon: "❓",
    items: [
      { id: "manual", visible: siempre },
    ],
  },
];

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
  const empresa = JSON.parse(localStorage.getItem("empresa") || "{}");
  const token = localStorage.getItem("token") || "";
  const grupos: GrupoEmpresaDto[] = JSON.parse(localStorage.getItem("grupos") || "[]");

  const [collapsed, setCollapsed] = useState(false);
  const [areaActiva, setAreaActiva] = useState("dashboard");
  const [itemActivo, setItemActivo] = useState<string | null>(null);
  const [permisos, setPermisos] = useState<string[]>([]);
  const [esAdminContexto, setEsAdminContexto] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [selectorEmpresa, setSelectorEmpresa] = useState(false);
  const [selectorGrupo, setSelectorGrupo] = useState(false);
  const [, setRefreshTick] = useState(0);

  // Refresca los combos de grupo/empresa (arriba a la derecha) sin necesidad de re-loguear,
  // por si se creó/eliminó/asoció algo desde Grupos de Empresas o Empresas.
  const refrescarAccesos = async () => {
    const datos = await getMisAccesos(usuario.usuarioId, token);
    if (datos) {
      localStorage.setItem("empresas", JSON.stringify(datos.empresas));
      localStorage.setItem("grupos", JSON.stringify(datos.grupos));
      setRefreshTick(t => t + 1);
    }
  };

  const todasEmpresas = JSON.parse(localStorage.getItem("empresas") || "[]");
  const grupoActivo = localStorage.getItem("grupoActivo");
  const grupoActivoId = grupoActivo ? Number(grupoActivo) : null;
  const empresasDisponibles = grupoActivo
    ? todasEmpresas.filter((e: any) => String(e.grupoEmpresaId) === grupoActivo)
    : todasEmpresas;

  useEffect(() => {
    const cargarPermisos = async () => {
      if (!usuario.usuarioId || !empresa.empresaId) return;
      const p = await getPermisos(usuario.usuarioId, empresa.empresaId, token);
      setPermisos(p);

      // esAdminContexto: Superusuario, GroupAdmin o EmpresaAdmin de la empresa activa.
      // Reusa /api/Usuarios/contexto (devuelve null si el solicitante no tiene ningún rol admin ahí).
      const contexto = await getUsuariosContexto(usuario.usuarioId, { empresaId: empresa.empresaId }, token);
      setEsAdminContexto(!!contexto);

      setCargando(false);
    };
    cargarPermisos();
  }, [empresa.empresaId]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleArea = (id: string) => {
    setAreaActiva(id);
    setItemActivo(null);
  };

  const handleCambiarEmpresa = async (e: any) => {
    setSelectorEmpresa(false);
    await seleccionarEmpresa(usuario.usuarioId, e.empresaId, token);
    localStorage.setItem("empresa", JSON.stringify({ empresaId: e.empresaId, nombre: e.nombre }));
    window.location.reload();
  };

  const handleCambiarGrupo = async (nuevoGrupoId: number) => {
    setSelectorGrupo(false);
    const empresasDelGrupo = todasEmpresas.filter((e: any) => e.grupoEmpresaId === nuevoGrupoId);
    if (empresasDelGrupo.length === 0) return;

    const ordenadas = [...empresasDelGrupo].sort((a: any, b: any) => {
      if (!a.ultimoAcceso) return 1;
      if (!b.ultimoAcceso) return -1;
      return new Date(b.ultimoAcceso).getTime() - new Date(a.ultimoAcceso).getTime();
    });
    const elegida = ordenadas[0];

    await seleccionarEmpresa(usuario.usuarioId, elegida.empresaId, token);
    localStorage.setItem("empresa", JSON.stringify({ empresaId: elegida.empresaId, nombre: elegida.nombre }));
    localStorage.setItem("grupoActivo", String(nuevoGrupoId));
    window.location.reload();
  };

  const ctx: VisCtx = { permisos, esSuperUsuario: !!usuario.esSuperUsuario, esAdmin: esAdminContexto || !!usuario.esSuperUsuario };

  const itemsVisibles = (area: Area) => area.items.filter(it => it.visible(ctx));
  const areaVisible = (area: Area) => area.standalone || itemsVisibles(area).length > 0;

  const areasVisibles = AREAS.filter(areaVisible);
  const area = AREAS.find(a => a.id === areaActiva);
  const items = area ? itemsVisibles(area) : [];

  if (cargando) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ color: "#888", fontSize: "15px" }}>{t("comun.cargando")}</div>
      </div>
    );
  }

  const renderContenido = () => {
    if (areaActiva === "dashboard") {
      return (
        <div style={styles.kpiGrid}>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Sistema</div>
            <div style={{ ...styles.kpiValue, color: "#27ae60" }}>Activo ✓</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Operaciones pendientes</div>
            <div style={styles.kpiValue}>0</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Tareas vencidas</div>
            <div style={{ ...styles.kpiValue, color: "#e74c3c" }}>0</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Empresa activa</div>
            <div style={{ ...styles.kpiValue, fontSize: "18px" }}>{empresa.nombre}</div>
          </div>
        </div>
      );
    }

    if (!itemActivo) {
      return <div style={styles.placeholder}>{t("comun.seleccionarOpcion")}</div>;
    }

    if (areaActiva === "grupo" && itemActivo === "gruposEmpresas") {
      return <GruposEmpresas usuarioId={usuario.usuarioId} token={token} onDatosCambiados={refrescarAccesos} />;
    }

    if (areaActiva === "grupo" && itemActivo === "general") {
      return (
        <GrupoGeneral
          grupoActivoId={grupoActivoId}
          nombreGrupo={grupos.find(g => g.grupoEmpresaId === grupoActivoId)?.nombre || ""}
          usuarioId={usuario.usuarioId}
          token={token}
          onDatosCambiados={refrescarAccesos}
        />
      );
    }

    if (areaActiva === "empresa" && itemActivo === "empresasAdmin") {
      return <EmpresasAdmin usuarioId={usuario.usuarioId} token={token} onDatosCambiados={refrescarAccesos} />;
    }

    if (areaActiva === "empresa" && itemActivo === "datosGenerales") {
      return (
        <Empresa
          empresaId={empresa.empresaId}
          token={token}
          puedeModificar={permisos.includes("parametros.empresas.modificar")}
        />
      );
    }

    if (areaActiva === "datos" && itemActivo === "planDeCuentas") {
      return (
        <PlanDeCuentas
          empresaId={empresa.empresaId}
          token={token}
          puedeAgregar={permisos.includes("contable.planDeCuentas.agregar")}
          puedeEditar={permisos.includes("contable.planDeCuentas.editar")}
          puedeEliminar={permisos.includes("contable.planDeCuentas.eliminar")}
        />
      );
    }

    if (areaActiva === "datos" && itemActivo === "monedas") {
      return (
        <Monedas
          empresaId={empresa.empresaId}
          token={token}
          puedeModificar={permisos.includes("contable.monedas.modificar")}
        />
      );
    }

    if (areaActiva === "seguridad" && itemActivo === "usuariosGrupo") {
      return (
        <Usuarios
          token={token}
          usuarioId={usuario.usuarioId}
          modo="grupo"
          grupoActivoId={grupoActivoId}
          empresaActivaId={empresa.empresaId}
        />
      );
    }

    if (areaActiva === "seguridad" && itemActivo === "usuariosEmpresa") {
      return (
        <Usuarios
          token={token}
          usuarioId={usuario.usuarioId}
          modo="empresa"
          grupoActivoId={grupoActivoId}
          empresaActivaId={empresa.empresaId}
        />
      );
    }

    if (areaActiva === "seguridad" && itemActivo === "rolesPermisos") {
      return (
        <AccesosPermisos
          token={token}
          usuarioId={usuario.usuarioId}
          empresaActivaId={empresa.empresaId}
        />
      );
    }

    return (
      <div style={styles.placeholder}>
        {t(`areas.${areaActiva}.items.${itemActivo}.label`)} — {t("comun.proximamente")}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* SIDEBAR */}
      <div style={{ ...styles.sidebar, width: collapsed ? 60 : 220 }}>
        <div style={styles.sidebarHeader}>
          <img src={logo} alt="KYBiz" style={styles.logo} />
          {!collapsed && (
            <div style={styles.brandName}>
              <span style={styles.brandKYB}>KYB</span><span style={styles.brandIz}>iz</span>
            </div>
          )}
        </div>

        <button style={styles.collapseBtn} onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? "→" : "←"}
        </button>

        <nav style={styles.nav}>
          {areasVisibles.map(a => (
            <div
              key={a.id}
              style={{
                ...styles.navItem,
                backgroundColor: areaActiva === a.id ? "#2B8FCC" : "transparent",
                justifyContent: collapsed ? "center" : "flex-start",
              }}
              onClick={() => handleArea(a.id)}
            >
              <span style={styles.navIconWrap}>
                <span style={styles.navIcon}>{a.icon}</span>
              </span>
              {!collapsed && <span style={styles.navLabel}>{t(`areas.${a.id}.label`)}</span>}
            </div>
          ))}
        </nav>

        <button style={styles.logoutBtn} onClick={handleLogout}>
          {collapsed ? "✕" : t("comun.cerrarSesion")}
        </button>
      </div>

      {/* SUBMENÚ */}
      {items.length > 0 && (
        <div style={styles.submenu}>
          <div style={styles.submenuTitle}>{t(`areas.${areaActiva}.label`)}</div>
          {items.map(it => (
            <div
              key={it.id}
              style={{
                ...styles.submenuItem,
                backgroundColor: itemActivo === it.id ? "#e8f4fc" : "transparent",
                color: itemActivo === it.id ? "#2B8FCC" : "#333",
                fontWeight: itemActivo === it.id ? 600 : 400,
              }}
              onClick={() => setItemActivo(it.id)}
            >
              {t(`areas.${areaActiva}.items.${it.id}.label`)}
            </div>
          ))}
        </div>
      )}

      {/* ÁREA PRINCIPAL */}
      <div style={styles.main}>
        <div style={styles.topbar}>
          <div>
            <span style={styles.pageTitle}>{t(`areas.${areaActiva}.label`)}</span>
            {itemActivo && (
              <span style={styles.pageSubtitle}>
                {" / "}{t(`areas.${areaActiva}.items.${itemActivo}.label`)}
              </span>
            )}
          </div>
          <div style={styles.topbarRight}>
            {grupos.length > 0 && (
              <div style={{ position: "relative" }}>
                <span
                  style={{ ...styles.grupoTag, cursor: grupos.length > 1 ? "pointer" : "default" }}
                  onClick={() => grupos.length > 1 && setSelectorGrupo(!selectorGrupo)}
                >
                  {grupos.find(g => String(g.grupoEmpresaId) === grupoActivo)?.nombre || "Grupo"} {grupos.length > 1 ? "▾" : ""}
                </span>
                {selectorGrupo && grupos.length > 1 && (
                  <div style={styles.dropdownEmpresas}>
                    {grupos.map(g => (
                      <div
                        key={g.grupoEmpresaId}
                        style={{
                          ...styles.dropdownItem,
                          fontWeight: String(g.grupoEmpresaId) === grupoActivo ? 700 : 400,
                          color: String(g.grupoEmpresaId) === grupoActivo ? "#2B8FCC" : "#333",
                        }}
                        onClick={() => handleCambiarGrupo(g.grupoEmpresaId)}
                      >
                        {g.nombre}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div style={{ position: "relative" }}>
              <span
                style={{ ...styles.empresaTag, cursor: empresasDisponibles.length > 1 ? "pointer" : "default" }}
                onClick={() => empresasDisponibles.length > 1 && setSelectorEmpresa(!selectorEmpresa)}
              >
                {empresa.nombre} {empresasDisponibles.length > 1 ? "▾" : ""}
              </span>
              {selectorEmpresa && (
                <div style={styles.dropdownEmpresas}>
                  {empresasDisponibles.map((e: any) => (
                    <div
                      key={e.empresaId}
                      style={{
                        ...styles.dropdownItem,
                        fontWeight: e.empresaId === empresa.empresaId ? 700 : 400,
                        color: e.empresaId === empresa.empresaId ? "#2B8FCC" : "#333",
                      }}
                      onClick={() => handleCambiarEmpresa(e)}
                    >
                      {e.nombre}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span style={styles.userInfo}>{usuario.nombre}</span>
          </div>
        </div>

        <div style={styles.content}>
          {renderContenido()}
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: "flex", minHeight: "100vh", backgroundColor: "#f0f2f5" },
  sidebar: { backgroundColor: "#1a1a2e", display: "flex", flexDirection: "column", transition: "width 0.2s ease", overflow: "hidden", flexShrink: 0 },
  sidebarHeader: { display: "flex", alignItems: "center", gap: "10px", padding: "20px 14px 16px 14px", borderBottom: "1px solid rgba(255,255,255,0.1)" },
  logo: { width: "32px", height: "32px", flexShrink: 0 },
  brandName: { display: "inline-flex", alignItems: "baseline", whiteSpace: "nowrap" },
  brandKYB: { fontFamily: "'Bebas Neue', sans-serif", fontSize: "26px", color: "#fff", lineHeight: 1 },
  brandIz: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: "14px", color: "#fff", lineHeight: 1, marginLeft: "1px" },
  collapseBtn: { background: "transparent", border: "none", color: "#aaa", fontSize: "16px", cursor: "pointer", padding: "8px 14px", textAlign: "right", width: "100%" },
  nav: { flex: 1, paddingTop: "8px", overflowY: "auto" as const },
  navItem: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", cursor: "pointer", borderRadius: "6px", margin: "2px 6px", transition: "background 0.15s" },
  navIconWrap: { width: "22px", display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 },
  navIcon: { fontSize: "16px", color: "#fff" },
  navLabel: { fontSize: "13px", fontWeight: 600, color: "#fff", whiteSpace: "nowrap" },
  logoutBtn: { margin: "12px 10px", padding: "10px", backgroundColor: "transparent", color: "#aaa", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "8px", cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap" },
  submenu: { width: "200px", backgroundColor: "#fff", borderRight: "1px solid #e0e0e0", flexShrink: 0, paddingTop: "16px", overflowY: "auto" as const },
  submenuTitle: { fontSize: "11px", fontWeight: 700, color: "#999", letterSpacing: "1px", textTransform: "uppercase" as const, padding: "0 16px 12px 16px", borderBottom: "1px solid #f0f0f0", marginBottom: "8px" },
  submenuItem: { padding: "10px 16px", fontSize: "13px", cursor: "pointer", borderRadius: "6px", margin: "2px 8px", transition: "background 0.15s" },
  main: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
  topbar: { backgroundColor: "#fff", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  pageTitle: { fontSize: "18px", fontWeight: 700, color: "#1a1a2e" },
  pageSubtitle: { fontSize: "15px", color: "#888" },
  topbarRight: { display: "flex", alignItems: "center", gap: "16px" },
  empresaTag: { backgroundColor: "#e8f4fc", color: "#2B8FCC", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 600 },
  grupoTag: { backgroundColor: "#fdf0e0", color: "#cc8b00", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 600 },
  userInfo: { fontSize: "13px", color: "#666" },
  content: { padding: "28px", flex: 1, overflow: "auto" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "20px" },
  kpiCard: { backgroundColor: "#fff", borderRadius: "12px", padding: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  kpiLabel: { fontSize: "12px", color: "#999", marginBottom: "10px", textTransform: "uppercase" as const, letterSpacing: "0.5px" },
  kpiValue: { fontSize: "28px", fontWeight: 700, color: "#1a1a2e" },
  placeholder: { backgroundColor: "#fff", borderRadius: "12px", padding: "60px", textAlign: "center" as const, color: "#bbb", fontSize: "15px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  dropdownEmpresas: { position: "absolute" as const, right: 0, top: "calc(100% + 8px)", backgroundColor: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 180, zIndex: 100, overflow: "hidden" },
  dropdownItem: { padding: "10px 16px", fontSize: 13, cursor: "pointer", borderBottom: "1px solid #f5f5f5" },
};
